import { create } from 'zustand';

export interface AckEntry {
  message_id: string;
  content_preview: string;
  command: string;
  status: 'queued' | 'delivered' | 'pasted' | 'failed';
  error?: string;
}

export interface WsMobileState {
  wsStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  ackStates: Record<string, AckEntry>;
  sessionExpired: boolean;
  appDisconnected: boolean;
  lastError: string | null;
  connect: (token: string) => void;
  disconnect: () => void;
  sendMessage: (content: string, command: string) => string; // returns message_id
  clearAcks: () => void;
}

let ws: WebSocket | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectDelayIndex = 0;
let currentToken: string | null = null;
let isManualDisconnect = false;

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000, 30000];

export const useWsStore = create<WsMobileState>((set, get) => ({
  wsStatus: 'disconnected',
  ackStates: {},
  sessionExpired: false,
  appDisconnected: false,
  lastError: null,

  connect: (token: string) => {
    if (!token) {
      set({ wsStatus: 'error', lastError: 'No token provided' });
      return;
    }

    isManualDisconnect = false;
    currentToken = token;

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
      ws = null;
    }

    set({ wsStatus: 'connecting', lastError: null });

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/s?token=${encodeURIComponent(token)}`;
      const socket = new WebSocket(wsUrl);
      ws = socket;

      socket.onopen = () => {
        reconnectDelayIndex = 0;
        set({ wsStatus: 'connected', lastError: null, appDisconnected: false });
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ack') {
            const { message_id, status, error } = data;
            if (message_id) {
              set((state) => {
                const existing = state.ackStates[message_id];
                if (!existing) return state;
                return {
                  ackStates: {
                    ...state.ackStates,
                    [message_id]: {
                      ...existing,
                      status: status || existing.status,
                      error: error !== undefined ? error : existing.error,
                    },
                  },
                };
              });
            }
          } else if (data.type === 'session_expired') {
            isManualDisconnect = true;
            if (reconnectTimeout) {
              clearTimeout(reconnectTimeout);
              reconnectTimeout = null;
            }
            if (ws) {
              ws.onopen = null;
              ws.onmessage = null;
              ws.onerror = null;
              ws.onclose = null;
              ws.close();
              ws = null;
            }
            set({ sessionExpired: true, wsStatus: 'disconnected' });
          } else if (data.type === 'app_disconnected') {
            set({ appDisconnected: true });
          } else if (data.type === 'app_connected') {
            set({ appDisconnected: false });
          }
        } catch {
          // Ignore invalid JSON or parsing errors
        }
      };

      socket.onerror = () => {
        set({ wsStatus: 'error', lastError: 'WebSocket error' });
      };

      socket.onclose = () => {
        if (!isManualDisconnect && currentToken) {
          set({ wsStatus: 'connecting' });
          const delay = RECONNECT_DELAYS[Math.min(reconnectDelayIndex, RECONNECT_DELAYS.length - 1)];
          reconnectDelayIndex++;
          reconnectTimeout = setTimeout(() => {
            if (currentToken && !isManualDisconnect) {
              get().connect(currentToken);
            }
          }, delay);
        } else {
          set({ wsStatus: 'disconnected' });
        }
      };
    } catch (err: any) {
      set({ wsStatus: 'error', lastError: err?.message || 'Failed to initialize WebSocket' });
    }
  },

  disconnect: () => {
    isManualDisconnect = true;
    currentToken = null;
    reconnectDelayIndex = 0;
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
      ws = null;
    }
    set({
      wsStatus: 'disconnected',
      sessionExpired: false,
      appDisconnected: false,
      lastError: null,
    });
  },

  sendMessage: (content: string, command: string) => {
    const message_id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2, 11) + Date.now().toString(36);

    const content_preview = content.length > 40 ? content.slice(0, 40) + '...' : content;

    const newAck: AckEntry = {
      message_id,
      content_preview,
      command,
      status: 'queued',
    };

    set((state) => ({
      ackStates: {
        ...state.ackStates,
        [message_id]: newAck,
      },
    }));

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: 'send',
          content,
          command,
          message_id,
        })
      );
    }

    return message_id;
  },

  clearAcks: () => {
    set({ ackStates: {} });
  },
}));
